// src/api/axios.js
import axios from "axios";
import { apiConfig } from "../../app.config.js";
import { useGlobalLoadingStore } from "@/components/helper/loading.js";
import { useToastStore } from "@/components/helper/toast";
import { useAuthStore } from "@/stores/auth.js";
import i18n from "@/i18n/index.js";

const api = axios.create({
  baseURL: apiConfig.baseUrl,
  headers: { "Content-Type": "application/json" },
});

// Store references
let loadingStore = null;
let toastStore = null;
let authStore = null;

export const initAxiosStores = () => {
  loadingStore = useGlobalLoadingStore();
  toastStore = useToastStore();
  authStore = useAuthStore();
};

// Token helpers
const getToken = () => localStorage.getItem("auth:token");
const getRefreshToken = () => localStorage.getItem("auth:refresh_token");

const setToken = (token) => {
  if (token) {
    localStorage.setItem("auth:token", token);
    api.defaults.headers.common.Authorization = `Bearer ${token}`;
  } else {
    localStorage.removeItem("auth:token");
    delete api.defaults.headers.common.Authorization;
  }
};

if (getToken()) setToken(getToken());

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((p) => (error ? p.reject(error) : p.resolve(token)));
  failedQueue = [];
};

// ================== INTERCEPTORS ==================

api.interceptors.request.use(
  (config) => {
    loadingStore?.start?.();
    return config;
  },
  (error) => {
    loadingStore?.stop?.();
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => {
    loadingStore?.stop?.();
    return response;
  },
  async (error) => {
    loadingStore?.stop?.();

    const originalRequest = error.config;

    if (error.response?.status !== 401) return Promise.reject(error);

    const requestUrl = originalRequest?.url || "";
    const isAuthRequest =
      requestUrl.includes("/auth/login") ||
      requestUrl.includes("/auth/refresh-token");

    // 401 dari login/refresh jangan dianggap session expired —
    // kalau dipaksa logout di sini, token hasil login baru bisa ikut terhapus.
    if (isAuthRequest) return Promise.reject(error);
    if (originalRequest._retry) return Promise.reject(error);

    // Request tanpa session (halaman login) tidak perlu auto-refresh/logout.
    if (!getToken() && !getRefreshToken()) {
      return Promise.reject(error);
    }

    const msg = (
      error.response?.data?.message ||
      error.response?.data?.error ||
      ""
    )
      .toString()
      .toLowerCase();

    if (
      msg.includes("invalid token") ||
      msg.includes("invalid refresh token") ||
      msg.includes("user not found")
    ) {
      await forceLogout("auth.sessionExpired");
      return Promise.reject(error);
    }

    // ================== AUTO REFRESH TOKEN ==================
    if (!originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = getRefreshToken();

      if (!refreshToken) {
        await forceLogout("auth.sessionExpired");
        return Promise.reject(error);
      }

      try {
        const { data } = await api.post("/api/auth/refresh-token", {
          refresh_token: refreshToken,
        });
        isRefreshing = false;

        const newAccessToken = data?.data?.access_token;
        const newRefreshToken = data?.data?.refresh_token || refreshToken;

        if (!newAccessToken) throw new Error("No access token from refresh");

        setToken(newAccessToken);
        localStorage.setItem("auth:refresh_token", newRefreshToken);

        processQueue(null, newAccessToken);
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        isRefreshing = false;
        await forceLogout("auth.sessionExpired");
        processQueue(refreshError, null);
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// ================== FUNGSI LOGOUT GLOBAL (dipakai saat refresh gagal) ==================
// const forceLogout = async (i18nKey = "auth.sessionExpired") => {
//   if (window.__isLoggingOut) return;
//   window.__isLoggingOut = true;

//   authStore?.clearSession?.();

//   localStorage.removeItem("app:activeTab");
//   localStorage.removeItem("app:openTabs");
//   location.hash = "dashboard";

//   toastStore?.show?.({
//     message: i18n.global.t(i18nKey),
//     type: "warning",
//     position: "top-center",
//     timeout: 6000,
//   });
// };

const forceLogout = async (i18nKey = "auth.sessionExpired") => {
  if (window.__isLoggingOut) return;
  window.__isLoggingOut = true;

  // bersihkan token & header axios
  localStorage.removeItem("auth:token");
  localStorage.removeItem("auth:refresh_token");
  delete api.defaults.headers.common.Authorization;

  authStore?.clearSession?.();

  localStorage.removeItem("app:activeTab");
  localStorage.removeItem("app:openTabs");
  location.hash = "dashboard";

  toastStore?.show?.({
    message: i18n.global.t(i18nKey),
    type: "warning",
    position: "top-center",
    timeout: 6000,
  });


  // penting: jangan ngunci selamanya
  setTimeout(() => (window.__isLoggingOut = false), 500);
};

export default api;
